import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useArchivedStudents, useRestoreStudent } from "hooks/api/students";
import { useStudentColumns } from "hooks/table/useStudentColumns";
import { useRole } from "hooks/common";
import { useActiveUser } from "hooks/redux/useActiveUser";
import { Divider } from "components/Common/Divider";
import { Empty } from "components/Common/Empty";
import { Table } from "components/Table";
import { StudySearch } from "../../../../components/StudySearch";
import { StudentsContent } from "content";
import { Route } from "constants/routes";
import { logError, logSuccess } from "utils/notifications";
import { TStudent } from "types/api/student";
import { TSelectOption } from "types/app/select";
import { EStudySearchTarget } from "types/app/study";
import "../../Students.styles.scss";

const PAGINATION_ENABLED = false;

export const StudentsArchive: React.FC = () => {
  const [searchValue, setSearchValue] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(0);

  const { isUserTeacher, isUserAdmin } = useRole();
  const { activeUser } = useActiveUser();

  const { data, isLoading, refetch } = useArchivedStudents({
    searchText: searchValue,
    pageNumber,
    pageSize: PAGINATION_ENABLED ? undefined : 99999,
    teacherId: isUserTeacher ? activeUser?.teacherId : null,
  });

  const { mutate: restoreStudent } = useRestoreStudent();

  const students: TStudent[] = useMemo(
    () => data?.content || [],
    [data?.content]
  );
  const isEmpty: boolean = students.length === 0;

  const searchCallback = useCallback(
    async (): Promise<TSelectOption<number>[]> => [],
    []
  );

  const handleOpenStudentProfileSubmit = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (_studentId: number) => {
      // Navigate to archived student profile - can be implemented if needed
    },
    []
  );

  const handleRestoreStudent = useCallback(
    (student: TStudent) => {
      restoreStudent(student.id, {
        onSuccess: () => {
          logSuccess(
            `Студента ${student.person.firstName} ${student.person.lastName} відновлено`
          );
          refetch();
        },
        onError: (error) => {
          logError({ error });
        },
      });
    },
    [restoreStudent, refetch]
  );

  const columns = useStudentColumns({
    onStudentNameClick: handleOpenStudentProfileSubmit,
    onEditClick: handleRestoreStudent,
    hideDelete: true,
    editButtonLabel: "Відновити",
  });

  return (
    <>
      <StudySearch
        searchTarget={EStudySearchTarget.Students}
        searchPlaceholder={StudentsContent.Search.PLACEHOLDER}
        searchCallback={searchCallback}
        setSearchValue={setSearchValue}
        hideAddButton
      />
      {isUserAdmin && (
        <div className="nb-sub-students-filter-links">
          <Link
            to={Route.App.Study.STUDENTS.path}
            className="nb-sub-students-filter-link"
          >
            Всі
          </Link>
          <Link
            to={Route.App.Study.STUDENTS_ARCHIVE.path}
            className="nb-sub-students-filter-link active"
          >
            Архів
          </Link>
        </div>
      )}
      <Divider margin="bottom" />
      {isEmpty && !isLoading ? (
        <Empty text="Архів студентів порожній" />
      ) : (
        <Table<TStudent>
          data={students}
          columns={columns}
          loading={isLoading}
          totalItems={data?.totalElements}
          onPageChange={setPageNumber}
          hideFooter={!PAGINATION_ENABLED}
        />
      )}
    </>
  );
};
